const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);
const hasValue = (value) => value !== null && value !== undefined && value !== "";
const sameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);
const getCompanyInputId = (record) => record?.companyInputId ?? record?.companyInput?.id ?? null;
const getEmployeeId = (record) => record?.employeeId ?? record?.employee?.employeeId ?? record?.employee?.id ?? null;
const getCoreEmployeeId = (employee) => employee?.employeeId ?? employee?.id ?? null;
const filterByInput = (records, companyInputId) => asArray(records).filter((record) => sameId(getCompanyInputId(record), companyInputId));

export const createSurveyHistorySeed = (fetched) => ({
  companyInput: asArray(fetched.companyInput).map((item) => ({ ...item })),
  companyDetail: asArray(fetched.companyDetail).map((item) => ({ ...item })),
  employees: asArray(fetched.employees),
  employeePayrolls: asArray(fetched.employeePayrolls),
  employeeAttendanceTimes: asArray(fetched.employeeAttendanceTimes),
  employeesAbsences: asArray(fetched.employeesAbsences),
  employeeEmplDetails: asArray(fetched.employeeEmplDetails),
  employeeNonOrderRelatedAttTime: asArray(fetched.employeeNonOrderRelatedAttTime),
});

const getEmployeeCollections = (source, companyInputId) => {
  const collections = {
    employeeAttendanceTimes: filterByInput(source.employeeAttendanceTimes, companyInputId),
    employeeEmplDetails: filterByInput(source.employeeEmplDetails, companyInputId),
    employeeNonOrderRelatedAttTime: filterByInput(source.employeeNonOrderRelatedAttTime, companyInputId),
    employeePayrolls: filterByInput(source.employeePayrolls, companyInputId),
    employeesAbsences: filterByInput(source.employeesAbsences, companyInputId),
  };
  const relatedEmployeeIds = new Set(Object.values(collections).flat().map(getEmployeeId).filter(hasValue).map(String));
  const employees = asArray(source.employees).filter((employee) => relatedEmployeeIds.has(String(getCoreEmployeeId(employee))));
  const availableCoreIds = new Set(employees.map(getCoreEmployeeId).filter(hasValue).map(String));
  const onlyWithCoreEmployee = (records) => records.filter((record) => availableCoreIds.has(String(getEmployeeId(record))));
  return {
    employees,
    ...Object.fromEntries(Object.entries(collections).map(([key, records]) => [key, onlyWithCoreEmployee(records)])),
  };
};

export async function loadPreviousSurveySnapshots({ history, activeSurveyId, loadOverheads }) {
  const previousInputs = [
    ...new Map(
      history.companyInput
        .filter((input) => hasValue(input?.id) && hasValue(input?.surveyId) && !sameId(input.surveyId, activeSurveyId))
        .map((input) => [String(input.surveyId), input])
    ).values(),
  ];

  const snapshots = await Promise.all(
    previousInputs.map(async (companyInput) => {
      let companyOverheads = [];

      try {
        companyOverheads = (await loadOverheads(companyInput.id)) || [];
      } catch (error) {
        console.error(`Could not load test-mode overhead history for companyInput=${companyInput.id}.`, error);
      }

      return {
        surveyId: companyInput.surveyId,
        surveyYear: companyInput.surveyYear ?? companyInput.year ?? companyInput.survey?.surveyYear ?? companyInput.survey?.year ?? null,
        companyInputId: companyInput.id,
        companyInput,
        companyDetail: history.companyDetail.find((detail) => sameId(detail?.companyInputId, companyInput.id)) || null,
        companyOverheads,
        ...getEmployeeCollections(history, companyInput.id),
      };
    })
  );

  return snapshots.sort((left, right) => Number(right.surveyYear ?? right.surveyId ?? 0) - Number(left.surveyYear ?? left.surveyId ?? 0));
}
